import React, { useEffect, useState } from "react";
import { generateTokenByApiKey } from "../api/login";
import { useDispatch } from "react-redux";
import { planPurchaseRequest, tryGetPlanTransaction } from "../api/plan";
import { getRandomString } from "../utils/helper";
import { IS_LOADING, SHOW_SNACKBAR } from "../utils/types";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

const tenantId = "T6611001XT8";
const planCode = "SugmyaSahaj";

const SinglePlan = () => {
  const dispatch = useDispatch();
  const [user, setUser] = useState({ gender: "MALE" });
  const [applicationNumber, setApplicationNumber] = useState("");
  const [token, setToken] = useState({});
  const [isChecked, setIsChecked] = useState(false);
  const [orderDetail, setOrderDetail] = useState({});
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [employeeId , setEmployeeId] = useState('')
  // Generate token on mount
  useEffect(() => {
    const keys = JSON.parse(process.env.REACT_APP_API_KEYS);
    const key = keys.find((f) => f.tenantId === tenantId);
    if (!key) return;

    dispatch(
      generateTokenByApiKey(
        {},
        "zealthix",
        {
          "x-tenant-id": key.tenantId,
          "X-Api-Key": key.key,
          "Content-Type": "application/json",
        },
        (t) => {
          if (t?.access_token) setToken(t);
        },
      ),
    );
  }, []);

  // Generate application number on mount
  useEffect(() => {
    setApplicationNumber(
      `O${getRandomString(2, planCode)}${Date.now().toString(36)}`.toUpperCase(),
    );
  }, []);

  const getTransaction = (transactionId) => {
    dispatch({
      type: IS_LOADING,
      payload: { type: IS_LOADING, payload: true },
    });
    tryGetPlanTransaction(transactionId, token?.access_token).then((res) => {
      dispatch({
        type: IS_LOADING,
        payload: { type: IS_LOADING, payload: false },
      });
      if (res?.status === "SUCCESS") {
        setShowCongratulations(true);
        setShowFailed(false);
      } else {
        if (!res?.transactionId) {
          dispatch({
            type: SHOW_SNACKBAR,
            payload: {
              type: "error",
              message:
                (res?.data?.description
                  ? res?.data?.description
                  : res?.data?.message) ?? "Please contact with administrator",
              open: true,
            },
          });
        }
        setShowFailed(true);
        setShowCongratulations(false);
      }
    });
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (!token?.access_token) {
      dispatch({
        type: SHOW_SNACKBAR,
        payload: {
          type: "error",
          message: "Authentication failed. Please refresh.",
          open: true,
        },
      });
      return;
    }

    const request = {
      planCode,
      agentId: employeeId ?? null,
      detail: { ...user, members: [] },
      tenantId,
      transactionId: applicationNumber,
      isSendPaymentLink: false,
    };

    dispatch(
      planPurchaseRequest(request, token?.access_token, (order) => {
        setOrderDetail(order);

        const options = {
          key: order.keyId,
          amount: order.payableAmount,
          currency: "INR",
          name: order.businessName,
          description: order.planName,
          order_id: order.paymentOrderId,
          handler: function (response) {
            getTransaction(order.transactionId);
          },
          prefill: {
            name: order.detail?.name,
            email: order.detail?.email,
            contact: order.detail?.phone,
          },
          notes: {
            transactionId: order.transactionId,
            planCode: order.planCode,
            tenantId: order.tenantId,
            type: "PLAN_PURCHASE",
          },
          theme: { color: "#5B1E3A" },
          modal: {
            ondismiss: function () {
              console.log("Payment modal dismissed by the user.");
            },
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();

        rzp.on("payment.failed", function (response) {
          console.log(`Payment failed: ${response.error.description}`);
        });
      }),
    );
  };

  // ── Success Screen ─────────────────────────────────────────────────────────
  if (showCongratulations) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8faff] px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-4" style={{ height: 180 }}>
            <DotLottieReact
              src="https://lottie.host/d9e78d76-07eb-4451-8248-8cc69cd747ce/kBHwmnleyx.lottie"
              loop
              autoplay
            />
          </div>
          <h4 className="text-xl font-bold text-[#0b1e40] mb-2">
            Congratulations!
          </h4>
          <p className="text-[#5a6a80] text-sm mb-1">
            You have successfully purchased the Plan{" "}
            <strong>{orderDetail?.planName}</strong>.
          </p>
          <p className="text-[#5a6a80] text-sm mt-3">
            You will receive app access credentials shortly. For queries, write
            to <strong>{orderDetail?.contactEmail}</strong>.
          </p>
        </div>
      </div>
    );
  }

  // ── Failed Screen ──────────────────────────────────────────────────────────
  if (showFailed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8faff] px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-4" style={{ height: 180 }}>
            <DotLottieReact
              src="https://lottie.host/452c79d0-93d5-4b58-931c-57a632afd4ea/XExsmpOfBI.lottie"
              loop
              autoplay
            />
          </div>
          <h3 className="text-[18px] font-[600] text-[#EA4335] mb-2">
            Payment Unsuccessful
          </h3>
          <p className="text-sm text-[#5a6a80] mb-3">
            Your payment for <strong>{orderDetail?.planName}</strong> was
            unsuccessful.
          </p>
          <div className="text-sm text-[#5a6a80] space-y-1">
            <p>
              <span className="font-medium">Amount:</span> ₹
              {orderDetail?.payableAmount}
            </p>
            <p>
              <span className="font-medium">Transaction ID:</span>{" "}
              {orderDetail?.transactionId}
            </p>
          </div>
          <button
            onClick={() => {
              setShowFailed(false);
              setApplicationNumber(
                `O${getRandomString(2, planCode)}${Date.now().toString(36)}`.toUpperCase(),
              );
            }}
            className="mt-5 w-full py-3 rounded-xl bg-gradient-to-l from-[#CC0404] via-[#5B1E3A] to-[#023365] text-white font-semibold text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── Main Form ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f8faff] flex flex-col gap-10 px-4 py-8">
      <div className="flex items-start">
        <img
          src="/assets/images/sugamya-finance-logo.png"
          alt="Aarogya"
          className="h-[40px] max-[305px]:h-[45px] block"
        />
      </div>
      <div className=" flex items-center justify-center xl:pb-10">
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-lg p-8">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-[22px] font-extrabold text-[#0b1e40] font-[Sora,sans-serif]">
              Sugmya Sahaj Plan
            </h2>
            <p className="text-[#5a6a80] text-sm mt-1">
              Fill in your details to complete the purchase
            </p>
            <div className="text-xs text-[#8a9ab0] mt-1">
              Application No:{" "}
              <span className="font-semibold">{applicationNumber}</span>
            </div>
            <div className="mt-3 inline-flex items-center gap-1 bg-[#f0fdf4] text-[#16a34a] text-xs font-semibold px-3 py-1 rounded-full border border-[#bbf7d0]">
              ₹499&nbsp; Benefits from Day 1
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {/* Name */}
            <div className="flex flex-col">
              <label className="text-[#656F7D] text-[13px] font-medium mb-1">
                Full Name *
              </label>
              <input
                type="text"
                placeholder="Enter your name"
                required
                className="h-[42px] px-3 rounded-lg border border-[#e0eaf5] bg-[#f8faff] text-sm text-[#0b1e40] outline-none focus:border-[#5B1E3A] transition"
                onChange={(e) => setUser({ ...user, name: e.target.value })}
              />
            </div>

            {/* Email */}
            <div className="flex flex-col">
              <label className="text-[#656F7D] text-[13px] font-medium mb-1">
                Email *
              </label>
              <input
                type="email"
                placeholder="Enter your email"
                required
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}"
                className="h-[42px] px-3 rounded-lg border border-[#e0eaf5] bg-[#f8faff] text-sm text-[#0b1e40] outline-none focus:border-[#5B1E3A] transition"
                onChange={(e) => setUser({ ...user, email: e.target.value })}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-[#656F7D] text-[13px] font-medium mb-1">
                Employee Id *
              </label>
              <input
                type="text"
                placeholder="Enter your employee ID"
                required
                className="h-[42px] px-3 rounded-lg border border-[#e0eaf5] bg-[#f8faff] text-sm text-[#0b1e40] outline-none focus:border-[#5B1E3A] transition"
                onChange={(e) => setEmployeeId(e.target.value)}
              />
            </div>

            {/* Phone */}
            <div className="flex flex-col">
              <label className="text-[#656F7D] text-[13px] font-medium mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                placeholder="10-digit mobile number"
                required
                pattern="[0-9]{10}"
                maxLength={10}
                className="h-[42px] px-3 rounded-lg border border-[#e0eaf5] bg-[#f8faff] text-sm text-[#0b1e40] outline-none focus:border-[#5B1E3A] transition"
                onInput={(e) => {
                  e.target.value = e.target.value
                    .replace(/[^0-9]/g, "")
                    .slice(0, 10);
                }}
                onChange={(e) => setUser({ ...user, phone: e.target.value })}
              />
            </div>

            {/* DOB + Gender — side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-[#656F7D] text-[13px] font-medium mb-1">
                  Date of Birth *
                </label>
                <input
                  type="date"
                  required
                  max={new Date().toISOString().split("T")[0]}
                  className="h-[42px] px-3 rounded-lg border border-[#e0eaf5] bg-[#f8faff] text-sm text-[#0b1e40] outline-none focus:border-[#5B1E3A] transition"
                  onChange={(e) => setUser({ ...user, dob: e.target.value })}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[#656F7D] text-[13px] font-medium mb-1">
                  Gender *
                </label>
                <select
                  required
                  value={user.gender}
                  className="h-[42px] px-3 rounded-lg border border-[#e0eaf5] bg-[#f8faff] text-sm text-[#0b1e40] outline-none focus:border-[#5B1E3A] transition"
                  onChange={(e) => setUser({ ...user, gender: e.target.value })}
                >
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                </select>
              </div>
            </div>

            {/* Declaration checkbox */}
            <label className="flex gap-2 items-start cursor-pointer mt-2">
              <input
                type="checkbox"
                className="mt-1 flex-shrink-0"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
              />
              <span className="text-[11px] text-[#8a9ab0] leading-relaxed">
                I hereby declare that I am in good health and entirely free from
                any mental or physical impairments, disease or condition. I have
                not been hospitalized or consulted any physician for reasons
                other than common cough, cold or flu, and I am not a habitual
                consumer of alcohol, tobacco or recreational drugs.
              </span>
            </label>

            {/* Submit */}
            <button
              type="submit"
              disabled={!isChecked}
              className={`w-full py-[14px] rounded-xl text-white text-sm font-semibold transition-all duration-200 ${
                isChecked
                  ? "bg-gradient-to-l from-[#CC0404] via-[#5B1E3A] to-[#023365] hover:opacity-90 hover:-translate-y-0.5"
                  : "bg-[#cdcdcd] cursor-not-allowed"
              }`}
            >
              Buy Now →
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SinglePlan;
